import { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import media from '@activebrands/core-web/config/media';
import useEvent from '@activebrands/core-web/libs/Events/useEvent';
import overlay from '@activebrands/core-web/libs/overlay';
import useScrollLock from '@activebrands/core-web/libs/scroll';
import { styled } from '@activebrands/core-web/libs/styletron';
import { getBreakpointIndex } from '@activebrands/core-web/utils/responsive';
import { Dialog } from '@base-ui/react/dialog';

const Wrapper = styled('aside', {
    position: 'fixed',
    color: 'var(--color-text)',
    outline: 'none',
    animationTimingFunction: 'var(--ease)',
    animationDuration: 'var(--duration-medium)',
    animationFillMode: 'forwards',

    '--start-bottom': 'translate(0, 100%)',
    '--start-top': 'translate(0, -100%)',
    '--start-left': 'translate(-100%, 0)',
    '--start-right': 'translate(100%, 0)',
    '--start-middle': 'translate(0, 0)',
    '--in-view': 'translate(0, 0)',
    '--in-middle': 'translate(-50%, -50%)',

    // These animate from the top in mobile
    '--from-rtl': 'var(--start-top)',
    '--from-ltr': 'var(--start-top)',

    [media.min['desktop.sm']]: {
        '--from-rtl': 'var(--start-right)',
        '--from-ltr': 'var(--start-left)',
    },
});

const animations = {
    slideRTL: {
        transform: 'var(--from-rtl)',
        animationFillMode: 'forwards',
        animationName: [
            {
                from: {
                    transform: 'var(--from-rtl)',
                },
                to: {
                    transform: 'var(--in-view)',
                },
            },
            {
                from: {
                    transform: 'var(--in-view)',
                },
                to: {
                    transform: 'var(--from-rtl)',
                },
            },
        ],
        bottom: 0,
        top: 'auto',
        right: 0,
    },
    slideLTR: {
        transform: 'var(--from-ltr)',
        animationFillMode: 'forwards',
        animationName: [
            {
                from: {
                    transform: 'var(--from-ltr)',
                },
                to: {
                    transform: 'var(--in-view)',
                },
            },
            {
                from: {
                    transform: 'var(--in-view)',
                },
                to: {
                    transform: 'var(--from-ltr)',
                },
            },
        ],
        bottom: 0,
        top: 'auto',
        left: 0,
    },
    slideBTT: {
        transform: 'var(--start-bottom)',
        animationFillMode: 'forwards',
        animationName: [
            {
                from: {
                    transform: 'var(--start-bottom)',
                },
                to: {
                    transform: 'var(--in-view)',
                },
            },
            {
                from: {
                    transform: 'var(--in-view)',
                },
                to: {
                    transform: 'var(--start-bottom)',
                },
            },
        ],
        bottom: 0,
        top: 'auto',
        right: 0,
    },
    slideBTM: {
        transform: 'var(--in-middle)',
        animationFillMode: 'forwards',

        animationName: [
            {
                from: {
                    opacity: '0',
                },
                to: {
                    opacity: '1',
                },
            },
            {
                from: {
                    opacity: '1',
                },
                to: {
                    opacity: '0',
                },
            },
        ],
        left: '50%',
    },
};

/**
 * @param {{
 *     $style?: object,
 *     animation?: string,
 *     animationDuration?: string,
 *     component: import('react').ElementType,
 *     id: string,
 *     isOpen?: boolean,
 *     isRendered?: boolean,
 *     lockScroll?: boolean,
 *     portalContainer?: HTMLElement | null,
 *     [key: string]: unknown,
 * }} props
 */
const AnimatedOverlay = ({
    $style = {},
    animation = 'slideRTL',
    animationDuration = 'var(--duration-medium)',
    component,
    id,
    isOpen = false,
    isRendered = false,
    lockScroll: lockPageScroll = false,
    portalContainer,
    ...rest
}) => {
    const [state, setState] = useState({
        isOpen,
        animate: isOpen,
        isRendered,
        props: undefined,
    });

    const { lockScroll, unlockScroll } = useScrollLock();

    const handleScrollLock = useCallback(
        (lock = true) => {
            if (lockPageScroll) {
                if (Array.isArray(lockPageScroll)) {
                    const arrLock = lockPageScroll.reduce((a, c, i) => {
                        if (c === null) {
                            return [...a, a[i - 1]];
                        }

                        return [...a, c];
                    }, []);

                    if (!arrLock[getBreakpointIndex()]) {
                        return;
                    }
                }

                lock ? lockScroll() : unlockScroll();
            }
        },
        [(lockPageScroll || '').toString()]
    );

    useEvent(`OVERLAY.OPEN_${id}`, props => {
        handleScrollLock(true);
        setState(prev => ({ ...prev, props, animate: true, isOpen: true, isRendered: true }));
    });

    useEvent(`OVERLAY.CLOSE_${id}`, () => {
        handleScrollLock(false);
        setState(prev => ({ ...prev, isOpen: false }));
    });

    useEffect(() => {
        return () => {
            overlay.close(id);
            unlockScroll();
        };
    }, [id]);

    const Overlay = component || state.props?.component;
    const overlayProps = { ...state.props, ...state.props?.props };

    if (!state.isRendered || !Overlay) {
        return null;
    }

    const animationStyle =
        animation && animations[animation]
            ? {
                  ...animations[animation],
                  animationName: animations[animation].animationName[state.isOpen ? 0 : 1],
                  animationDuration: animationDuration,
              }
            : {};

    if (!state.animate) {
        // styletron dosn't handle null or undefined on animationName so we need to delete it.
        delete animationStyle.animationName;
    }

    const onAnimationEnd = state.isOpen
        ? undefined
        : () => setState(prev => ({ ...prev, props: undefined, isRendered }));

    const handleOpenChange = (open, eventDetails) => {
        if (!open && eventDetails?.reason === 'escape-key') {
            overlay.close(id);
        }
    };

    return (
        <Dialog.Root modal="trap-focus" open={state.isOpen} onOpenChange={handleOpenChange}>
            <Dialog.Portal keepMounted container={portalContainer}>
                <Dialog.Popup
                    aria-describedby={undefined}
                    aria-labelledby={`overlay-${id}-title`}
                    aria-modal="true"
                    finalFocus={closeType => closeType === 'keyboard'}
                    id={`overlay-${id}`}
                    render={
                        <Wrapper
                            $style={{
                                ...animationStyle,
                                zIndex: 'var(--zindex-overlay)',
                                top: 'var(--height-header)',
                                height: 'calc(100% - var(--height-header))',
                                ...$style,
                            }}
                            onAnimationEnd={onAnimationEnd}
                        />
                    }
                >
                    <Overlay
                        {...rest}
                        {...overlayProps}
                        id={id}
                        titleId={`overlay-${id}-title`}
                        isOpen={state.isOpen}
                    />
                </Dialog.Popup>
            </Dialog.Portal>
        </Dialog.Root>
    );
};

AnimatedOverlay.propTypes = {
    $style: PropTypes.object,
    animation: PropTypes.oneOf(['slideLTR', 'slideBTT', 'slideRTL', 'slideBTM']),
    animationDuration: PropTypes.string,
    component: PropTypes.oneOfType([PropTypes.elementType, PropTypes.element]),
    id: PropTypes.string.isRequired,
    isOpen: PropTypes.bool,
    isRendered: PropTypes.bool,
    lockScroll: PropTypes.bool,
    portalContainer: PropTypes.any,
    width: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
};

export default AnimatedOverlay;
